import React, { useState, useCallback, useEffect } from "react";
import { Row, Col, Toast, ToastContainer } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";

//Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

//API Imports
import { getCallsListApi, getDeleteTeamCallApi, bulkDeleteCallApi } from "./calls-api";
import CommonFilter from "../common/commonfilter.js";
import Loader from "../common/Loader.jsx";

//SVG Images
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import Plus from "../../assets/svg/add.svg";
import Dropdown from "../../assets/svg/dropdown.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import Copy from "../../assets/svg/copy_ics_a.svg";
import DeleteModal from "../common/delete-modal";
import { loginRequest } from "../../msalConfig";
import { useMsal } from "@azure/msal-react";
import { getDeleteApi } from "../common/delete-api";
import RecordsPerPage from "../common/RecordsPerPage.jsx";

const CallsList = () => {
  const { instance, accounts } = useMsal();

  //API URL
  const apiUrl = process.env.REACT_APP_API_URL;

  const options = [
    "Code",
    "Title",
    "Subject",
    "Call For",
    "Call Type",
    "Start Time",
    "End Time",
    "Duration",
  ];

  //Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  //Navigate
  const navigate = useNavigate();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Calls");
  const hasEditPermission = canEdit(rolePermissionList, "Calls");
  const hasDeletePermission = canDelete(rolePermissionList, "Calls");
  const hasViewPermission = canView(rolePermissionList, "Calls");

  //Search
  const [search, setSearch] = useState("");

  //Filters
  const [showFilter, setShowFilter] = useState(false);
  const [filterParams, setFilterParams] = useState("");

  //Create Calls Dropdown
  const [open, setOpen] = useState(false);

  //Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(() =>
    Object.fromEntries(options.map((label) => [label, true])),
  );

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  const columnsGridTemplate = visibleOptions.map(() => "1fr").join(" ") || "1fr";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  const [isTokenRequired, setTokenRequired] = useState(false);
  //Pagination
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    const newOffset = (event.selected * itemsPerPage) % callsList.length;
    console.log(
      `User requested page number ${
        event.selected
      }, which is offset ${newOffset}&skip=${itemOffset * itemsPerPage}`,
    );
    setItemOffset(event.selected);
  }, []);

  //Get Calls List
  const [callsList, setCallsList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const getCallsList = async () => {
    setListLoading(true);
    try {
      const response = await getCallsListApi(
        `?search=${search}&limit=${itemsPerPage}&skip=${
          itemOffset * itemsPerPage
        }${filterParams}`,
      );
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        // const formatted = formatCallsData(response.data.calls);
        setCallsList(
          response.data.calls.map((call) => ({
            id: call.id,
            Code: call.call_code,
            Title: call.title,
            Subject: call.subject,
            "Call For": call.call_for,
            "Call Type": call.call_type,
            "Start Time": call.start_time,
            "End Time": call.end_time,
            Duration: call.duration,
            teams_id: call.teams_id,
          })),
        );
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    getCallsList();
  }, [search, itemsPerPage, itemOffset, filterParams]);

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedCallsList = React.useMemo(() => {
    if (!sortColumn) return callsList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...callsList].sort((a, b) => {
      const aStr = (a[sortColumn] ?? "").toString().toLowerCase();
      const bStr = (b[sortColumn] ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [callsList, sortColumn, sortDirection]);

  useEffect(() => {
    setSelectedRows([]);
    setAllSelected(false);
  }, [search, itemsPerPage, itemOffset, filterParams, selectedColumns]);

  useEffect(() => {
    return () => {
      setSelectedRows([]);
      setAllSelected(false);
    };
  }, []);

  //Delete
  const [deleteRow, setDeleteRow] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteRow != null) {
      setIsOpenDelete(true);
    }
  }, [deleteRow]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
  };
  const onSuccessDelete = (response) => {
    setDeleteRow(null);

    // setSnackbarMessage(response.message);
    // setSnackbarStatus(response.success);
    // handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      getCallsList();
    }
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    if (callsList.length > 0 && selectedRows.length === callsList.length) {
      setAllSelected(true);
    } else {
      setAllSelected(false);
    }
  }, [selectedRows, callsList]);

  // handle checkbox logic
  const toggleAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(callsList.map((call) => call.id));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const deleteCallFunc = async (id) => {
    // 🔒 Permission check for delete
    if (!hasDeletePermission) {
      setSnackbarMessage("You don't have permission to delete calls");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    setDeleteRow(id);
  };

  // Snackbar for copy feedback
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Copy to clipboard function
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setSnackbarMessage(`✓ Copied: ${code}`);
      setSnackbarStatus(true);
      handleSnackbarShow();
    }).catch(() => {
      setSnackbarMessage("✗ Failed to copy");
      setSnackbarStatus(false);
      handleSnackbarShow();
    });
  };

  const modalStyle = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      opacity: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "#ffffff",
      padding: "40px",
      borderRadius: "16px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 1)",
      width: "436px",
      height: "228px",
      textAlign: "center",
    },
  };

  const deleteApi = async () => {
    try {
      let response;
      if (selectedRows.length > 0) {
        response = await bulkDeleteCallApi(selectedRows);
        if (response?.data?.success === true) {
          setSelectedRows([]);
          setAllSelected(false);
          onSuccessDelete(response.data);
        }
      } else {
        response = await getDeleteApi(`web/call/delete/${deleteRow.id}`);
        if (response?.status === 200 || response?.status === 500) {
          onSuccessDelete(response.data);
        }
      }
      return response?.data;
    } catch (err) {
      console.error("Error deleting:", err);
    } finally {
    }
  };
  const deleteTeamCallApi = async () => {
    const request = {
      scopes: ["Calendars.Read"],
      account: accounts[0],
    };

    try {
      console.log("teams delete called 1");
      instance.loginPopup(loginRequest).then(async (tokenResponse) => {
        const response = await getDeleteTeamCallApi(
          `web/call/delete/${deleteRow.id}`,
          {
            thirdparty: "Microsoft-Teams", //Microsoft-Teams
            accesstoken: tokenResponse.accessToken,
          },
        );
        if (response?.status === 200 || response?.status === 500) {
          onSuccessDelete(response.data);
        }
        return response.data;
      });
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };
  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        backgroundColor: "#f8f9fb",
      }}
    >
      <CommonFilter
        type="calls"
        open={showFilter}
        onClose={() => setShowFilter(false)}
        onSubmit={(data) => {
          setFilterParams(data);
          setItemOffset(0);
        }}
      />
      {/*  {isOpenDelete && <DeleteModal deleteUrl={`web/call/delete/${deleteRow}`} onSuccess={onSuccessDelete} onCancel={cancelDelete} />} */}
      {isOpenDelete && (
        <div style={modalStyle.overlay} backdrop="static">
          <div style={modalStyle.modal}>
            <div style={{ color: "#ffffff" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#1E1E1E",
                  }}
                >
                  Delete{" "}
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "16px",
                    fontWeight: "400",
                    color: "#1E1E1E",
                  }}
                >
                  You won’t able to access your record after deleting. Are you
                  sure want to delete?
                </div>

                <div
                  style={{
                    flex: 12,
                    marginTop: "8px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "start",
                    justifyContent: "start",
                  }}
                >
                  <div
                    onClick={cancelDelete}
                    style={{
                      cursor: "pointer",
                      width: "158px",
                      height: "44px",
                      marginLeft: "8px",
                      marginRight: "8px",
                      borderRadius: "8px",
                      display: "flex",
                      flex: 6,
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #E5E5E5",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#1E1E1E",
                      }}
                    >
                      Cancel
                    </div>
                  </div>
                  <div
                    onClick={() => {
                      if (selectedRows.length > 0) {
                        deleteApi();
                      } else if (deleteRow) {
                        if (
                          deleteRow.teams_id != null &&
                          deleteRow.teams_id !== ""
                        ) {
                          deleteTeamCallApi();
                        } else {
                          deleteApi();
                        }
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      width: "158px",
                      height: "44px",
                      marginLeft: "8px",
                      marginRight: "8px",
                      borderRadius: "8px",
                      display: "flex",
                      flex: 6,
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#FF4444",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#FFFFFF",
                      }}
                    >
                      Delete
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom: "32px" }}>
        <Row>
          <Col sm={12} md={6}>
            <div className="fs-14 fw-600 text-dkblue">
              Calls - {records}
            </div>
          </Col>

          <Col sm={12} md={6} lg={3}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "100px",
                border: "0.8px solid #e5e5e5",
                padding: "9px 16px",
                backgroundColor: "#ffffff",
                height: "40px",
              }}
            >
              <img src={Search} alt="search" style={{ width: "16px" }} />
              <input
                type="search"
                placeholder="Search..."
                className="no-focus-border"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: "14px",
                  flex: 1,
                  backgroundColor: "transparent",
                }}
              />
            </div>
          </Col>

          <Col sm={1} style={{ display: "flex", justifyContent: "center" }}>
            <img src={Filter} style={{ height: "40px", cursor: "pointer" }} onClick={() => setShowFilter(true)} />
          </Col>

          <Col
            sm={2}
            md={6}
            lg={2}
            style={{ display: "flex", justifyContent: "center" }}
          >
            {selectedRows.length === 0 && hasCreatePermission ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "8px 16px",
                    borderTopLeftRadius: "100px",
                    borderBottomLeftRadius: "100px",
                    borderTopRightRadius: "100px",
                    borderBottomRightRadius: "100px",
                    borderBottom: "3px solid #1723C2",
                    background:
                      "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                    height: "40px",
                    width: "fit-content",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "8px" }}
                    onClick={() => navigate("/calls/create")}
                  >
                    <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                    <div className="fs-15 fw-600 text-white">Create</div>
                  </div>
                  <div
                    onClick={() => setOpen(!open)}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div
                      style={{
                        width: "1px",
                        height: "40px",
                        backgroundColor: "#ffffff",
                      }}
                    ></div>
                    <div style={{ paddingLeft: "16px" }}>
                      <img
                        src={Dropdown}
                        style={{ width: "10px" }}
                        alt="Dropdown"
                      />
                    </div>
                  </div>
                </div>

                {open && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50px",
                      backgroundColor: "#ffffff",
                      padding: "16px",
                      borderRadius: "16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      width: "100%",
                    }}
                  >
                    <div style={{ padding: "8px 0", cursor: "pointer" }} onClick={() => { setOpen(false); navigate("/calls/create", { state: { callFrom: "Lead" } }); }}>Lead</div>
                    <div style={{ padding: "8px 0", cursor: "pointer" }} onClick={() => { setOpen(false); navigate("/calls/create", { state: { callFrom: "Contact" } }); }}>Contact</div>
                    <div style={{ padding: "8px 0", cursor: "pointer" }} onClick={() => { setOpen(false); navigate("/calls/create", { state: { callFrom: "Account" } }); }}>Account</div>
                    <div style={{ padding: "8px 0", cursor: "pointer" }} onClick={() => { setOpen(false); navigate("/calls/create", { state: { callFrom: "Deal" } }); }}>Deal</div>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "0 20px",
                  borderRadius: "50px",
                  borderBottom: "3px solid #B31717",
                  background:
                    "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                  height: "40px",
                  width: "auto",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setIsOpenDelete(true);
                }}
              >
                <div className="fs-15 fw-600 text-white">
                  Delete ({selectedRows.length})
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <Row>
          <Col sm={12}>
            <div
              style={{
                borderRadius: "16px",
                border: "1px solid #ebebeb",
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
              className="table-scroll-container"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
              {/* Header */}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  borderBottom: "1px solid #ebebeb",
                }}
              >
                <div
                  style={{
                    width: "72px",
                    padding: "10px 24px",
                    flexShrink: 0,
                    borderRight: "1px solid #ebebeb",
                  }}
                >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleAll(e)}
                    />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                  {visibleOptions.map((label) => (
                    <div
                      key={label}
                      onClick={() => handleSortClick(label)}
                      title={`Sort by ${label}`}
                      style={{
                        minWidth: 0,
                        padding: "10px 16px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderRight: "1px solid #ebebeb",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                        <div
                          className="fs-12 fw-600 text-ltgrey"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label.toUpperCase()}
                        </div>
                        <span className="fs-12 fw-600" style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}>
                          {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                        </span>
                      </div>
                      <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                    </div>
                  ))}
                </div>

                <div
                  onClick={() => setShowModal(true)}
                  style={{
                    width: "72px",
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img src={Customize} alt="customize" />
                </div>
              </div>

              {/* Data Rows */}
              {listLoading ? (
                <div style={{ width: "100%", padding: "40px 0" }}>
                  <Loader height="120px" />
                </div>
              ) : sortedCallsList.length === 0 ? (
                <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                  No calls found
                </div>
              ) : (
                sortedCallsList.map((call, rowIndex) => (
                <div
                  key={"Call" + rowIndex}
                  onMouseEnter={() => setHoveredIndex(rowIndex)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: "1px solid #ebebeb",
                    backgroundColor: hoveredIndex === rowIndex ? "#f8f9fb" : "#ffffff",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "72px",
                      padding: "10px 24px",
                      flexShrink: 0,
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(call.id)}
                      onChange={() => toggleRow(call.id)}
                      disabled={!anyColumnSelected}
                    />
                  </div>
                  <div
                    style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: hasViewPermission ? "pointer" : "not-allowed" }}
                    onClick={() => {
                      if (hasViewPermission) {
                        navigate("/calls/view", { state: { id: call.id } });
                      }
                    }}
                  >
                    {visibleOptions.map((label) => (
                      <div
                        key={label}
                        title={call[label]}
                        className="text-dkblue fs-14 fw-400"
                        style={{
                          minWidth: 0,
                          padding: "10px 16px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          borderRight: "1px solid #ebebeb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {call[label] || "-"}
                        </div>
                        {label === "Code" && call[label] && (
                          <img
                            src={Copy}
                            alt="Copy"
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              flexShrink: 0,
                              opacity: 0.6,
                              transition: "opacity 0.2s",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyCode(call[label]);
                            }}
                            onMouseEnter={(e) => (e.target.style.opacity = "1")}
                            onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
                            title="Copy code"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ width: "72px", flexShrink: 0 }} />
                  {hoveredIndex === rowIndex && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "12px",
                        transform: "translateY(-50%)",
                        zIndex: 1,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {hasEditPermission && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/calls/create", {
                                state: { id: call.id },
                              });
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              border: "0.6px solid #E5E5E5",
                              cursor: "pointer",
                              justifyContent: "center",
                              width: "32px",
                              height: "32px",
                              borderRadius: "100px",
                              background: "#FFFFFF",
                            }}
                            title="Edit Call"
                          >
                            <EditRow />
                          </div>
                        )}
                        {hasEditPermission && hasDeletePermission && <span style={{ marginLeft: "16px" }}></span>}
                        {hasDeletePermission && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCallFunc(call);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              border: "0.6px solid #E5E5E5",
                              cursor: "pointer",
                              justifyContent: "center",
                              width: "32px",
                              height: "32px",
                              borderRadius: "100px",
                              background: "#FFFFFF",
                            }}
                            title="Delete Call"
                          >
                            <DeleteRow />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                ))
              )}

              {showModal && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#fff",
                      padding: "24px",
                      borderRadius: "12px",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                      width: "300px",
                      maxHeight: "80vh",
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "16px",
                        marginBottom: "16px",
                      }}
                    >
                      Select Columns
                    </div>
                    {options.map((label, index) => (
                      <label
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "10px",
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedColumns[label]}
                          onChange={() => toggleColumn(label)}
                          style={{ marginRight: "8px" }}
                        />
                        {label}
                      </label>
                    ))}
                    <button
                      onClick={() => setShowModal(false)}
                      style={{
                        marginTop: "16px",
                        width: "100%",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "none",
                        backgroundColor: "#3D4AF5",
                        color: "#fff",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <Row>
          <Col>
            <RecordsPerPage
              value={itemsPerPage}
              onChange={(event) => {
                setItemsPerPage(event.target.value);
              }}
            />
          </Col>
          <Col>
            <div className="d-flex justify-content-end">
              <ReactPaginate
                previousLabel={<img src={lar} className="ar3" alt="previous" />}
                nextLabel={<img src={rar} className="ar3" alt="next" />}
                breakLabel={"..."}
                pageCount={pageCount + 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
              />
            </div>
          </Col>
        </Row>

        <ToastContainer position="top-end">
          <Toast
            className="d-inline-block m-1"
            bg={snackbarStatus === true ? "success" : "danger"}
            show={showSnackbar}
            onClose={handleSnackbarClose}
            delay={3000}
            autohide
          >
            <Toast.Body style={{ color: "#ffffff" }}>
              {snackbarMessage}
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
    </div>
  );
};

export default CallsList;
